import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { cloneElement } from 'react';
import { baseTextParser, } from '../utils/text/baseTextParser/baseTextParser';
import { _measureText, getCachedTokenSize, } from '../utils/text/tokenMeasureCache/tokenMeasureCache';
import { getTextStyles } from './text';
import { createTruncatedTextElement } from './textEllipsis';
export const _parseBreak = (element, { maxLines, lineGap, width, singleSpaceWidth, offset: initOffset }) => {
    const offset = Object.assign({}, initOffset);
    const state = { isLineLimitReached: false, lineHighestWord: 0, lines: 0 };
    const { children: elements } = element.props;
    const handleWordBreaks = (element) => {
        const { props: { text = '' }, } = element;
        const textByWord = text.split(/\s|\n/g);
        const elements = textByWord.map((word, index) => cloneElement(element, { key: `${word}-${index}`, text: word }));
        return baseTextParser({
            offset,
            elements,
            width,
            onTextExceedsWidth,
            onTextFits,
        });
    };
    const handleWordHyphens = (element, sizes) => {
        const { props } = element;
        const { text = '' } = props;
        const hyphenSize = getCachedTokenSize('-', props);
        const hyphenPoint = sizes.findIndex((token) => token.width + hyphenSize.width + offset.x > width) - 1;
        /*
         * Not even the first character fits.
         * This means that this is a left over from an already broken-down part.
         */
        if (hyphenPoint === -1) {
            offset.x = 0;
            offset.y += state.lineHighestWord + lineGap;
            state.lines++;
            return baseTextParser({
                offset,
                elements: [element],
                width,
                onTextExceedsWidth,
                onTextFits,
            });
        }
        const firstTextPart = text.slice(0, hyphenPoint);
        const secondTextPart = text.slice(hyphenPoint);
        const wordOffsetX = offset.x;
        const wordOffsetY = offset.y;
        const wordSize = sizes[sizes.length - 1];
        offset.x = 0;
        offset.y += state.lineHighestWord + lineGap;
        state.lineHighestWord = wordSize.height;
        state.lines++;
        return [
            cloneElement(element, {
                text: `${firstTextPart}-`,
                position: { x: wordOffsetX, y: wordOffsetY },
            }),
            ...baseTextParser({
                offset,
                elements: [
                    cloneElement(element, {
                        text: secondTextPart,
                    }),
                ],
                width,
                onTextExceedsWidth,
                onTextFits,
            }),
        ];
    };
    const onTextExceedsWidth = ({ element, sizes, }) => {
        if (state.isLineLimitReached) {
            return { elements: [], offset };
        }
        const { props } = element;
        const { text = '' } = props;
        const isTextMultipleWords = /\s|\n/g.test(text);
        if (isTextMultipleWords) {
            const elements = handleWordBreaks(element);
            return { elements, offset };
        }
        const style = getTextStyles(props);
        const wordSize = _measureText(text, style);
        if (wordSize.width > width) {
            const elements = handleWordHyphens(element, sizes);
            return { elements, offset };
        }
        const currentOffsetY = offset.y;
        const currentOffsetX = offset.x;
        offset.y += state.lineHighestWord + lineGap;
        state.lineHighestWord = wordSize.height;
        state.lines++;
        if (state.lines >= maxLines) {
            state.isLineLimitReached = true;
            offset.x = 0;
            const truncatedTextElement = createTruncatedTextElement({
                element,
                sizes,
                offset: { x: currentOffsetX, y: currentOffsetY },
                maxWidth: width,
            });
            return { elements: [truncatedTextElement], offset };
        }
        const elements = [
            cloneElement(element, {
                position: { x: 0, y: offset.y },
            }),
        ];
        offset.x = (wordSize.width + singleSpaceWidth) | 0;
        return { elements, offset };
    };
    const onTextFits = ({ element, sizes, isLastElement, }) => {
        if (state.lines >= maxLines || state.isLineLimitReached) {
            return { elements: [], offset };
        }
        const { props } = element;
        const wordOffset = sizes[sizes.length - 1];
        state.lineHighestWord = Math.max(state.lineHighestWord, wordOffset.height);
        const ellipsisSize = getCachedTokenSize('...', props);
        const isTextWithEllipsisTooWide = offset.x + singleSpaceWidth + wordOffset.width + ellipsisSize.width >=
            width;
        /*
         * Figure out whether the text would still fit if it needed to be truncated.
         */
        if (state.lines + 1 >= maxLines &&
            isTextWithEllipsisTooWide &&
            !isLastElement) {
            const truncatedTextElement = createTruncatedTextElement({
                element,
                sizes,
                offset,
                maxWidth: width,
            });
            state.isLineLimitReached = true;
            return { elements: [truncatedTextElement], offset };
        }
        const elements = [
            cloneElement(element, {
                position: { x: offset.x, y: offset.y },
            }),
        ];
        offset.x += (wordOffset.width + singleSpaceWidth) | 0;
        return { elements, offset };
    };
    const children = baseTextParser({
        offset,
        elements,
        width,
        onTextExceedsWidth,
        onTextFits,
    });
    return {
        children,
        height: offset.y + state.lineHighestWord,
        lines: state.lines,
    };
};
export function TextBreak({ children }) {
    return _jsx(_Fragment, { children: children });
}
